import React, { useState, useEffect } from 'react';
import { DataGrid, GridColDef, GridSortModel } from '@mui/x-data-grid';
import axios from 'axios';
import { Entry } from './types/entry';
import './styles/App.css';
import Cookies from 'js-cookie';
import moment from 'moment';
import { Console } from 'console';

const App: React.FC = () => {
  // State for grid data, sorting model, filter parameters, and pagination
  const [rows, setRows] = useState<Entry[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filter, setFilter] = useState({ field: '', value: '' });
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(10);

  const [apitoken, setApiToken] = useState<string | null>(Cookies.get('apitoken') || null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [columnWidths, setColumnWidths] = useState<{ [key: string]: number }>({});

  const requestNewToken = () => {
    const token = prompt('Bitte geben Sie das API-Token ein:');
    if (token) {
      Cookies.set('apitoken', token);
      setApiToken(token);
      fetchEntries(token);
    }
  };

  useEffect(() => {
    fetchEntries(); // Fetch data if token exists
  }, [apitoken, sortModel, filter, page, pageCount]);

  useEffect(() => {
    const savedVisibility = Cookies.get('columnVisibilityModel');
    const savedWidths = Cookies.get('columnWidths');
    if (savedVisibility) setColumnVisibilityModel(JSON.parse(savedVisibility));
    if (savedWidths) setColumnWidths(JSON.parse(savedWidths));
  }, []);

  const fetchEntries = async (token: string | null = apitoken) => {
    const sortField = sortModel.length ? sortModel[0].field : '';
    const sortOrder = sortModel.length ? sortModel[0].sort : '';

    try {
      const response = await axios.get('/entries', {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          filterField: filter.field,
          filterValue: filter.value,
          sortField,
          sortOrder,
          page,
          pageCount,
        },
      });
      setRows(response.data.entries);
      setRowCount(response.data.total);
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        await axios.get('/auth', { withCredentials: true });
        const newToken = Cookies.get("apitoken") || null;
        setApiToken(newToken);
        fetchEntries(newToken);
      } else {
        console.error("Error fetching data:", error);
      }
    }
  };

  useEffect(() => {
    Cookies.set('columnVisibilityModel', JSON.stringify(columnVisibilityModel), { expires: 7 });
    Cookies.set('columnWidths', JSON.stringify(columnWidths), { expires: 7 });
  }, [columnVisibilityModel, columnWidths]);

  // Define the columns for the DataGrid
  const columns: GridColDef[] = [
    { field: '_id', headerName: 'Id', width: columnWidths['_id'] || 150 },
    { field: 'ErrorMessage', headerName: 'ErrorMessage', width: columnWidths['ErrorMessage'] },
    { field: 'SerialNumber', headerName: 'Serial Number', width: columnWidths['SerialNumber'] },
    { field: 'DepotName', headerName: 'DepotName', width: columnWidths['DepotName'] },
    { field: 'Timestamp', headerName: 'Timestamp', width: columnWidths['Timestamp'],
      valueGetter: (params:any) => new Date(params), valueFormatter: (params:any) => moment(params).format('DD.MM.YYYY HH:mm:ss') },
    { field: 'ComputerName', headerName: 'Computer Name', width: columnWidths['ComputerName'] },
    { field: 'Program', headerName: 'Program', width: columnWidths['Program'] },
    { field: 'TwsUserName', headerName: 'TwsUserName', width: columnWidths['TwsUserName'] },
    { field: 'Version', headerName: 'Version', width: columnWidths['Version'] },
    { field: 'MessageKind', headerName: 'MessageKind', width: columnWidths['MessageKind'] },
    { field: 'User', headerName: 'User', width: columnWidths['User'] },
  ];

  return (
    <div className="app-container">
      <DataGrid
        paginationMode='server'
        rows={rows}
        rowCount={rowCount}
        columns={columns}
        paginationModel={{ pageSize: pageCount, page: page - 1 }} // Use paginationModel for page and pageSize
        pageSizeOptions={[5, 10, 25, 50, 100]}
        sortingOrder={['asc', 'desc']}
        sortModel={sortModel}
        onSortModelChange={(model) => setSortModel(model)}
        getRowId={(row) => row._id}
        pagination
        onPaginationModelChange={(model) => {
          setPage(model.page + 1); // Model page is 0-based
          setPageCount(model.pageSize);
        }}
        initialState={{
          columns: {
            columnVisibilityModel: columnVisibilityModel,
          },
          pagination: {
            paginationModel: { pageSize: 25, page: 0 },
          },
        }}
        autosizeOptions={{
          columns: ['_id', 'Timestamp', 'DepotName'],
          includeOutliers: true,
          includeHeaders: false,
        }}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
        onColumnWidthChange={(params) => {
          setColumnWidths((prevWidths) => ({
            ...prevWidths,
            [params.colDef.field]: params.width,
          }));
        }}
      />
      <div style={{ margin: '20px 0' }}>
        <label>Filter Field:</label>
        <input
          type="text"
          value={filter.field}
          onChange={(e) => setFilter({ ...filter, field: e.target.value })}
        />
        <label>Filter Value:</label>
        <input
          type="text"
          value={filter.value}
          onChange={(e) => setFilter({ ...filter, value: e.target.value })}
        />
      </div>
    </div>
  );
};

export default App;